class FxSliderComponent extends HTMLElement {
  // Class constants for magic numbers
  static MOBILE_BREAKPOINT = 798;
  static DRAG_THRESHOLD = 50;
  static MOBILE_GAP = 4;
  static DESKTOP_GAP = 8;
  static SCROLL_STEP = 2;
  static MOBILE_PRODUCTS_TO_SHOW = 2;
  static MAX_DOTS_RETRY = 10;
  static DOTS_RETRY_DELAY = 100;

  constructor() {
    super();
    this.slider = this.querySelector('[id^="Slider-"]');
    this.items = Array.from(this.querySelectorAll('[id^="Slide-"]'));
    this.currentIndex = 0;
    this.changePerSecond = parseInt(this.dataset.changePerSecond, 10) || 4;
    this.productsToShow = parseInt(this.dataset.productToShow, 10) || 4;
    this.maxTranslatePercentage = -(this.items.length * 100 - this.productsToShow * 100);
    if (this.maxTranslatePercentage > 0) this.maxTranslatePercentage = 0;
    this.maxIndex = Math.ceil(this.items.length / this.productsToShow) - 1;
    if (this.maxIndex < 0) this.maxIndex = 0;
    this.isGeoLocation = this.dataset.geoLocation === 'true';

    this.isDragging = false;
    this.startX = 0;
    this.currentTranslate = 0;
    this.prevTranslate = 0;
    this.animationID = 0;

    this.autoRotate = false;
    this.autoRotateInterval = null;

    // Store bound event handlers to prevent memory leaks
    this.boundHandleResize = this.debounce(this.handleResize.bind(this), 200);
    this.boundHandleStartDrag = this.handleStartDrag.bind(this);
    this.boundHandleMove = this.handleMove.bind(this);
    this.boundHandleEndDrag = this.handleEndDrag.bind(this);
    this.boundPreventNativeDrag = this.preventNativeDrag.bind(this);
    this.boundStopAutoRotate = this.stopAutoRotate.bind(this);
    this.boundStartAutoRotate = this.startAutoRotate.bind(this);

    // Cache for display:contents check
    this.itemsWithContentsCache = null;
    this.dotsRetryCount = 0;
  }

  connectedCallback() {
    // Validate slider element exists
    if (!this.slider) {
      console.error('FxSliderComponent: Slider element not found');
      return;
    }

    requestAnimationFrame(() => {
      this.createDots();
      this.addEventListeners();

      // Start auto-rotate if enabled
      if (this.dataset.autoRotate === 'true') {
        this.autoRotate = true;
        this.startAutoRotate();
      }
    });

    // Observe changes to the `data-auto-rotate` attribute
    this.observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'data-auto-rotate') {
          this.handleAutoRotateChange();
        }
      });
    });

    this.observer.observe(this, {
      attributes: true,
      attributeFilter: ['data-auto-rotate'],
    });
  }

  disconnectedCallback() {
    this.removeEventListeners();
    this.stopAutoRotate();
    if (this.observer) {
      this.observer.disconnect();
    }
    // Clear cache
    this.itemsWithContentsCache = null;
  }

  handleAutoRotateChange() {
    const wasAutoRotate = this.autoRotate;
    this.autoRotate = this.dataset.autoRotate === 'true';

    // Dynamically add/remove hover listeners based on autoRotate state
    if (this.autoRotate && !wasAutoRotate) {
      this.slider.addEventListener('mouseenter', this.boundStopAutoRotate);
      this.slider.addEventListener('mouseleave', this.boundStartAutoRotate);
      this.startAutoRotate();
    } else if (!this.autoRotate && wasAutoRotate) {
      this.slider.removeEventListener('mouseenter', this.boundStopAutoRotate);
      this.slider.removeEventListener('mouseleave', this.boundStartAutoRotate);
      this.stopAutoRotate();
    }
  }

  addEventListeners() {
    if (!this.slider) return;

    // Add event listeners for drag and resize
    this.slider.addEventListener('touchstart', this.boundHandleStartDrag);
    this.slider.addEventListener('touchmove', this.boundHandleMove);
    this.slider.addEventListener('touchend', this.boundHandleEndDrag);

    this.slider.addEventListener('mousedown', this.boundHandleStartDrag);
    window.addEventListener('mousemove', this.boundHandleMove);
    window.addEventListener('mouseup', this.boundHandleEndDrag);
    this.slider.addEventListener('dragstart', this.boundPreventNativeDrag);

    window.addEventListener('resize', this.boundHandleResize);

    // Add auto-rotate hover listeners if enabled
    if (this.autoRotate) {
      this.slider.addEventListener('mouseenter', this.boundStopAutoRotate);
      this.slider.addEventListener('mouseleave', this.boundStartAutoRotate);
    }
  }

  removeEventListeners() {
    if (!this.slider) return;

    // Remove all event listeners to prevent memory leaks
    this.slider.removeEventListener('touchstart', this.boundHandleStartDrag);
    this.slider.removeEventListener('touchmove', this.boundHandleMove);
    this.slider.removeEventListener('touchend', this.boundHandleEndDrag);

    this.slider.removeEventListener('mousedown', this.boundHandleStartDrag);
    window.removeEventListener('mousemove', this.boundHandleMove);
    window.removeEventListener('mouseup', this.boundHandleEndDrag);
    this.slider.removeEventListener('dragstart', this.boundPreventNativeDrag);

    window.removeEventListener('resize', this.boundHandleResize);

    // Remove auto-rotate listeners (using stored bound functions)
    this.slider.removeEventListener('mouseenter', this.boundStopAutoRotate);
    this.slider.removeEventListener('mouseleave', this.boundStartAutoRotate);
  }

  // Helper method to check if element has ancestor with display:contents
  hasAncestorDisplayContents(el) {
    let currentEl = el.parentElement;
    while (currentEl) {
      if (getComputedStyle(currentEl).display === 'contents') {
        return true;
      }
      currentEl = currentEl.parentElement;
    }
    return false;
  }

  // Helper method to get items with display:contents ancestors (cached)
  getItemsWithContents() {
    if (this.itemsWithContentsCache === null) {
      this.itemsWithContentsCache = this.items.filter((item) => this.hasAncestorDisplayContents(item));
    }
    return this.itemsWithContentsCache;
  }

  // Helper method to determine if viewport is mobile
  isMobileViewport() {
    return window.innerWidth <= FxSliderComponent.MOBILE_BREAKPOINT;
  }

  // Helper method to get number of products to show
  getProductsToShow() {
    return this.isMobileViewport() ? FxSliderComponent.MOBILE_PRODUCTS_TO_SHOW : this.productsToShow;
  }

  // Helper method to get gap size
  getGapSize() {
    // The slider list uses the theme's .grid gap (--grid-*-horizontal-spacing),
    // so measure the rendered gap instead of assuming a fixed value
    if (this.slider) {
      const computedGap = parseFloat(window.getComputedStyle(this.slider).columnGap);
      if (!Number.isNaN(computedGap)) return computedGap;
    }
    return this.isMobileViewport() ? FxSliderComponent.MOBILE_GAP : FxSliderComponent.DESKTOP_GAP;
  }

  // Helper method to get items count based on geolocation setting
  getActiveItemsCount() {
    if (this.isGeoLocation) {
      return this.getItemsWithContents().length;
    }
    return this.items.length;
  }

  handlePrev() {
    // Navigate to the previous slide
    if (this.currentIndex > 0) {
      this.currentIndex -= 1;
    }
    this.updateItemsPosition();
    this.updateDots();
    this.resetAutoRotate();
  }

  handleNext() {
    // Navigate to the next slide
    if (this.currentIndex < this.maxIndex) {
      this.currentIndex += 1;
    } else {
      this.currentIndex = 0; // Loop back to the first slide
    }
    this.updateItemsPosition();
    this.updateDots();
    this.resetAutoRotate();
  }

  getCurrentSliderTranslateX() {
    const transform = window.getComputedStyle(this.slider).transform;
    if (transform !== 'none') {
      // Support both 2D and 3D transform matrices
      const match = transform.match(/matrix.*\((.+)\)/);
      if (match) {
        const values = match[1].split(', ');
        // For 2D matrix, translateX is at index 4
        // For 3D matrix (matrix3d), translateX is at index 12
        return values.length === 6 ? parseFloat(values[4]) : parseFloat(values[12]);
      }
    }
    return 0;
  }

  preventNativeDrag(event) {
    // Cards are <a><img></a>, which browsers treat as draggable. Chrome drops the pending
    // native drag when mousemove is default-prevented (handleMove does that); Firefox does
    // not, and once its native drag starts no further mousemove reaches the window, so the
    // carousel froze after ~20px and the image ghost followed the cursor instead.
    event.preventDefault();
  }

  handleStartDrag(event) {
    if (!this.slider) return;

    this.isDragging = true;
    const isMobile = this.isMobileViewport();
    this.startX = event.touches ? event.touches[0].clientX : event.clientX;
    this.startY = event.touches ? event.touches[0].clientY : event.clientY;
    this.prevTranslate = this.getCurrentSliderTranslateX();
    this.slider.style.transition = 'none';
    this.animationID = requestAnimationFrame(this.animation.bind(this));
    this.slider.classList.add('active');
    this.stopAutoRotate();
  }

  handleMove(event) {
    if (!this.isDragging) return;

    const currentX = event.touches ? event.touches[0].clientX : event.clientX;
    const currentY = event.touches ? event.touches[0].clientY : event.clientY;
    const deltaX = currentX - this.startX;
    const deltaY = currentY - this.startY;

    // Prevent default if horizontal movement is dominant
    if (Math.abs(deltaX) > Math.abs(deltaY)) {
      event.preventDefault();
    }

    // Check boundaries after preventDefault
    if (this.currentIndex === 0 && deltaX > 0) return;
    if (this.currentIndex === this.maxIndex && deltaX < 0) return;

    this.currentTranslate = this.prevTranslate + deltaX;

    if (deltaX !== 0) {
      if (this.slider.style.overflow !== 'visible') {
        this.slider.style.overflow = 'visible';
      }
      this.enableCardWrappers();
    }
  }

  handleEndDrag() {
    if (!this.isDragging || !this.slider) return;

    cancelAnimationFrame(this.animationID);
    this.isDragging = false;
    this.slider.classList.remove('active');

    const movedBy = this.currentTranslate - this.prevTranslate;

    // Page by drag distance: a drag past the threshold always moves at least one page,
    // and every further step's worth of distance adds another, clamped to the range.
    if (Math.abs(movedBy) > FxSliderComponent.DRAG_THRESHOLD) {
      const metrics = this.calculateSliderMetrics();
      const step = metrics ? metrics.step : 0;
      const pages = step > 0 ? Math.max(1, Math.round(Math.abs(movedBy) / step)) : 1;
      const direction = movedBy < 0 ? 1 : -1;
      this.currentIndex = Math.min(this.maxIndex, Math.max(0, this.currentIndex + direction * pages));
    }

    // Absorb the slider's drag offset into each item's transform
    // so clearing the slider transform doesn't cause a visual snap
    const dragOffset = this.currentTranslate;
    this.items.forEach((item) => {
      const match = item.style.transform.match(/translateX\((.+)px\)/);
      const currentItemX = match ? parseFloat(match[1]) : 0;
      item.style.transition = 'none';
      item.style.transform = `translateX(${currentItemX + dragOffset}px)`;
    });

    // Reset slider container transform (no visual jump since items compensate)
    this.slider.style.transform = '';
    this.slider.style.overflow = 'hidden';

    // Force reflow so the intermediate position is applied
    void this.slider.offsetHeight;

    // Re-enable item transitions and animate to the target position
    this.items.forEach((item) => {
      item.style.transition = '';
    });

    this.updateItemsPosition();
    this.updateDots();
    this.currentTranslate = 0;
    this.prevTranslate = 0;

    this.disableCardWrappers();
    this.resetAutoRotate();
  }

  calculateSliderMetrics() {
    // Early return if no items
    if (this.items.length === 0) return null;

    const gap = this.getGapSize();
    const itemWidth = this.items[0]?.offsetWidth || 0;

    if (itemWidth === 0) return null;

    const activeItemsCount = this.getActiveItemsCount();

    if (activeItemsCount === 0) return null;

    // Calculate widths correctly: items don't have trailing gaps
    // Total width = all items + gaps between them (not after last item)
    const totalWidth = activeItemsCount * itemWidth + Math.max(0, activeItemsCount - 1) * gap;

    // Step = distance to scroll (moving past SCROLL_STEP items and their gaps)
    const step = FxSliderComponent.SCROLL_STEP * itemWidth + FxSliderComponent.SCROLL_STEP * gap;

    // Use the slider element's width (the <ul> with overflow:hidden) so the last
    // page aligns flush to the right edge. this.offsetWidth is unreliable because
    // fx-slider-component is a custom element that may be display:inline.
    const containerWidth = this.slider.offsetWidth;
    let maxTranslateX = totalWidth - containerWidth;

    if (maxTranslateX < 0) maxTranslateX = 0;

    return {
      itemWidth,
      gap,
      step,
      maxTranslateX,
      maxIndex: Math.ceil(maxTranslateX / step),
    };
  }

  updateItemsPosition() {
    const metrics = this.calculateSliderMetrics();
    if (!metrics) return;

    this.maxIndex = metrics.maxIndex;
    let translateX = this.currentIndex * metrics.step;

    if (translateX > metrics.maxTranslateX) {
      translateX = metrics.maxTranslateX;
      this.currentIndex = this.maxIndex;
    }

    this.items.forEach((item) => {
      item.style.transform = `translateX(-${translateX}px)`;
    });
  }

  createDots() {
    const dotsContainer = this.querySelector('.dots');

    if (!dotsContainer) {
      // Retry with exponential backoff, max 10 attempts
      if (this.dotsRetryCount < FxSliderComponent.MAX_DOTS_RETRY) {
        this.dotsRetryCount++;
        console.warn(`Dots container not found, retrying (${this.dotsRetryCount}/${FxSliderComponent.MAX_DOTS_RETRY})`);
        setTimeout(() => this.createDots(), FxSliderComponent.DOTS_RETRY_DELAY * this.dotsRetryCount);
      } else {
        console.error('Dots container not found after maximum retries');
      }
      return;
    }

    // Clear existing dots
    dotsContainer.innerHTML = '';

    // Use pixel-based metrics to determine dot count, ensuring dots
    // match the actual number of navigable positions on all viewports
    const metrics = this.calculateSliderMetrics();
    if (!metrics) return;

    this.maxIndex = metrics.maxIndex;
    const totalDots = metrics.maxIndex + 1;

    if (totalDots <= 1) return;

    // Use DocumentFragment for better performance
    const fragment = document.createDocumentFragment();

    for (let i = 0; i < totalDots; i++) {
      const dot = document.createElement('button');
      dot.classList.add('dot');
      dot.setAttribute('type', 'button');
      dot.setAttribute('aria-label', `Go to slide ${i + 1}`);
      dot.setAttribute('aria-current', i === this.currentIndex ? 'true' : 'false');

      if (i === this.currentIndex) {
        dot.classList.add('active');
      }

      // Add click handler for dot navigation
      dot.addEventListener('click', () => {
        this.currentIndex = i;
        this.updateItemsPosition();
        this.updateDots();
        this.resetAutoRotate();
      });

      fragment.appendChild(dot);
    }

    dotsContainer.appendChild(fragment);
  }

  updateDots() {
    // Update active state of dots
    const dots = this.querySelectorAll('.dots .dot');
    dots.forEach((dot, index) => {
      const isActive = index === this.currentIndex;
      dot.classList.toggle('active', isActive);
      dot.setAttribute('aria-current', isActive ? 'true' : 'false');
    });
  }

  enableCardWrappers() {
    // Temporarily disable interaction with card wrappers
    this.items.forEach((item) => {
      const cardWrapper = item.querySelector('.card-wrapper');
      if (cardWrapper) cardWrapper.classList.add('disabled-element');
    });
  }

  disableCardWrappers() {
    // Re-enable interaction with card wrappers
    this.items.forEach((item) => {
      const cardWrapper = item.querySelector('.card-wrapper');
      if (cardWrapper) cardWrapper.classList.remove('disabled-element');
    });
  }

  animation() {
    if (this.isDragging && this.slider) {
      this.slider.style.transform = `translateX(${this.currentTranslate}px)`;
      requestAnimationFrame(this.animation.bind(this));
    }
  }

  startAutoRotate() {
    // Start auto-rotation
    if (this.autoRotateInterval) return;
    this.autoRotateInterval = setInterval(() => {
      this.handleNext();
    }, this.changePerSecond * 1000);
  }

  stopAutoRotate() {
    // Stop auto-rotation
    if (this.autoRotateInterval) {
      clearInterval(this.autoRotateInterval);
      this.autoRotateInterval = null;
    }
  }

  resetAutoRotate() {
    // Reset auto-rotation timer
    if (this.autoRotate) {
      this.stopAutoRotate();
      this.startAutoRotate();
    }
  }

  debounce(func, delay) {
    // Utility function to debounce events
    let timer;
    return (...args) => {
      clearTimeout(timer);
      timer = setTimeout(() => {
        func.apply(this, args);
      }, delay);
    };
  }

  handleResize() {
    // Handle window resize events
    // Invalidate cache on resize as layout may change
    this.itemsWithContentsCache = null;
    this.updateItemsPosition();
    this.createDots();
  }

  reinit() {
    // Reset transforms on all slide elements (including hidden ones)
    this.querySelectorAll('[id^="Slide-"]').forEach((item) => {
      item.style.transform = '';
    });

    // Re-collect only visible items (offsetParent is null for display:none)
    this.items = Array.from(this.querySelectorAll('[id^="Slide-"]')).filter((item) => item.offsetParent !== null);

    // Reset to first slide
    this.currentIndex = 0;

    // Invalidate caches
    this.itemsWithContentsCache = null;

    // Recalculate max values
    this.maxIndex = this.items.length > 0 ? Math.ceil(this.items.length / this.getProductsToShow()) - 1 : 0;
    if (this.maxIndex < 0) this.maxIndex = 0;

    // Recalculate positions and dots
    this.updateItemsPosition();
    this.createDots();
    this.resetAutoRotate();
  }
}

customElements.define('fx-slider-component', FxSliderComponent);
